import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import { Button, Card, Col, Divider, Drawer, Input, Row, Select, Space, Statistic, Table, Tag, Typography, message } from 'antd'
import { EditOutlined, FilePdfOutlined, PlusOutlined } from '@ant-design/icons'

import Layout from '../App/layout'
import LayoutContentWrapper from '../../components/utility/layoutWrapper'
import ContentLayout from '../../components/utility/layoutContent'

import Attachments from './Attachments'
import LoanAgreementForm from './LoanAgreementForm'
import { openLoanAgreementPdf } from './openPdf'
import {
  DEFAULT_SORT,
  INTEREST_STATUSES,
  LOAN_AGREEMENTS_QUERY,
  PRINCIPLE_STATUSES,
  SORTS,
  STATUS_COLORS
} from './graphql'

const PAGE_SIZE = 25
const NEW_ID = 'new'
const BASE_PATH = '/loan_agreements'
const DATE_FORMAT = 'DD/MM/YYYY'
const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm'

const formatDate = value => (value ? dayjs(value).format(DATE_FORMAT) : '-')

const money = value => (value ? value.display : '-')

const SUMMARY_ROWS = [
  ['principlePending', 'principlePaid', 'principleTotal'],
  ['interestPending', 'interestPaid', 'interestTotal'],
  ['pendingAmount', 'paidAmount', 'totalAmount']
]

const Summary = ({ summary }) => {
  const { t } = useTranslation('loanAgreements')

  return (
    <Card size='small' style={{ marginBottom: 16 }} title={t('summaryTitle')}>
      <Typography.Paragraph type='secondary'>{t('summaryHint')}</Typography.Paragraph>
      {SUMMARY_ROWS.map((row, index) => (
        <React.Fragment key={row[0]}>
          {index > 0 && <Divider style={{ margin: '12px 0' }} />}
          <Row gutter={16}>
            {row.map(key => (
              <Col key={key} md={8} xs={24}>
                <Statistic
                  precision={2}
                  title={t(`summary.${key}`)}
                  value={summary && summary[key] ? summary[key].value : 0}
                />
              </Col>
            ))}
          </Row>
        </React.Fragment>
      ))}
    </Card>
  )
}

Summary.propTypes = {
  summary: PropTypes.object
}

Summary.defaultProps = {
  summary: null
}

const StatusCell = ({ status, paidDate }) => {
  const { t } = useTranslation('loanAgreements')

  return (
    <Space direction='vertical' size={2}>
      <Tag color={STATUS_COLORS[status]}>{t(`statuses.${status}`)}</Tag>
      {paidDate && <Typography.Text type='secondary'>{formatDate(paidDate)}</Typography.Text>}
    </Space>
  )
}

StatusCell.propTypes = {
  paidDate: PropTypes.string,
  status: PropTypes.string.isRequired
}

StatusCell.defaultProps = {
  paidDate: null
}

const LoanAgreementsIndex = () => {
  const { t } = useTranslation('loanAgreements')
  const history = useHistory()
  const { id: drawerId } = useParams()

  const [page, setPage] = useState(1)
  const [borrowerInput, setBorrowerInput] = useState('')
  const [borrowerQuery, setBorrowerQuery] = useState(undefined)
  const [principleStatus, setPrincipleStatus] = useState(undefined)
  const [interestStatus, setInterestStatus] = useState(undefined)
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [pdfLoadingId, setPdfLoadingId] = useState(null)

  useEffect(() => {
    document.title = 'Loan Agreements | OSH CRM'
  }, [])

  const { data, loading, refetch } = useQuery(LOAN_AGREEMENTS_QUERY, {
    variables: {
      page,
      per: PAGE_SIZE,
      borrowerQuery,
      principleStatus,
      interestStatus,
      sort
    },
    fetchPolicy: 'cache-and-network'
  })

  const result = (data && data.loanAgreements) || {}

  const resetPage = setter => (value) => {
    setter(value)
    setPage(1)
  }

  const handleClearFilters = () => {
    setBorrowerInput('')
    setBorrowerQuery(undefined)
    setPrincipleStatus(undefined)
    setInterestStatus(undefined)
    setSort(DEFAULT_SORT)
    setPage(1)
  }

  const handleTableChange = (pagination, _filters, sorter) => {
    const sortKey = sorter && sorter.order && SORTS[sorter.columnKey]
    const nextSort = sortKey ? sortKey[sorter.order] : DEFAULT_SORT

    if (nextSort !== sort) {
      setSort(nextSort)
      setPage(1)
      return
    }

    setPage(pagination.current)
  }

  const sortOrderFor = (columnKey) => {
    const options = SORTS[columnKey]
    if (sort === options.ascend) return 'ascend'
    if (sort === options.descend) return 'descend'
    return null
  }

  const handleDownload = async (record) => {
    setPdfLoadingId(record.id)
    const opened = await openLoanAgreementPdf(record.id)
    setPdfLoadingId(null)
    if (!opened) message.error(t('downloadFailed'))
  }

  const closeDrawer = () => history.push(BASE_PATH)

  const handleSaved = () => {
    refetch()
    closeDrawer()
  }

  const columns = [
    {
      title: t('sequenceNumber'),
      dataIndex: 'id',
      key: 'id'
    },
    {
      title: t('issueDate'),
      dataIndex: 'issueDate',
      key: 'issueDate',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      sortOrder: sortOrderFor('issueDate'),
      render: formatDate
    },
    {
      title: t('borrower'),
      dataIndex: 'borrower',
      key: 'borrower',
      render: value => value || '-'
    },
    {
      title: t('amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: money
    },
    {
      title: t('interestRate'),
      dataIndex: 'interestRate',
      key: 'interestRate',
      align: 'right',
      render: value => (value === null || value === undefined ? '-' : `${value}%`)
    },
    {
      title: t('interestPeriod'),
      key: 'interestPeriod',
      render: (_value, record) => (
        <span>
          {record.interestPeriod === null ? '-' : t('daysCount', { count: record.interestPeriod })}
          {record.dueDate && <Typography.Text type='secondary'> ({formatDate(record.dueDate)})</Typography.Text>}
        </span>
      )
    },
    {
      title: t('interestPerDay'),
      dataIndex: 'interestPerDay',
      key: 'interestPerDay',
      align: 'right',
      render: money
    },
    {
      title: t('totalInterest'),
      dataIndex: 'totalInterest',
      key: 'totalInterest',
      align: 'right',
      render: money
    },
    {
      title: t('withHoldingTax'),
      key: 'withHoldingTax',
      align: 'right',
      render: (_value, record) => (
        <Space direction='vertical' size={0}>
          <span>{money(record.withHoldingTax)}</span>
          {record.withHoldingTaxRate !== null && (
            <Typography.Text type='secondary'>({record.withHoldingTaxRate})</Typography.Text>
          )}
        </Space>
      )
    },
    {
      title: t('totalAmount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      align: 'right',
      render: money
    },
    {
      title: t('documents'),
      key: 'documents',
      render: (_value, record) => (
        <Space direction='vertical' size={4}>
          <Attachments attachments={record.signedDocuments} />
          <Divider style={{ margin: 0 }} />
          <Attachments attachments={record.slips} />
        </Space>
      )
    },
    {
      title: t('paymentNote'),
      dataIndex: 'paymentNote',
      key: 'paymentNote',
      render: value => (value ? <Typography.Text style={{ whiteSpace: 'pre-line' }}>{value}</Typography.Text> : '-')
    },
    {
      title: t('principleStatus'),
      key: 'principleStatus',
      render: (_value, record) => <StatusCell paidDate={record.principlePaidDate} status={record.principleStatus} />
    },
    {
      title: t('interestStatus'),
      key: 'interestStatus',
      render: (_value, record) => <StatusCell paidDate={record.interestPaidDate} status={record.interestStatus} />
    },
    {
      title: t('updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      sortOrder: sortOrderFor('updatedAt'),
      render: value => (value ? dayjs(value).format(DATE_TIME_FORMAT) : '-')
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      render: (_value, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => history.push(`${BASE_PATH}/${record.id}`)}
            size='small'
          >
            {t('edit')}
          </Button>
          <Button
            icon={<FilePdfOutlined />}
            loading={pdfLoadingId === record.id}
            onClick={() => handleDownload(record)}
            size='small'
          >
            {t('download')}
          </Button>
        </Space>
      )
    }
  ]

  const formId = drawerId && drawerId !== NEW_ID ? drawerId : null

  return (
    <Layout>
      <LayoutContentWrapper breadcrumbs={[
        { path: BASE_PATH, name: t('title') }
      ]}>
        <ContentLayout>
          <Summary summary={result.summary} />

          <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} wrap>
            <Space wrap>
              <Input.Search
                allowClear
                onChange={event => setBorrowerInput(event.target.value)}
                onSearch={value => resetPage(setBorrowerQuery)(value.trim() || undefined)}
                placeholder={t('searchBorrower')}
                style={{ width: 240 }}
                value={borrowerInput}
              />
              <Select
                allowClear
                onChange={resetPage(setPrincipleStatus)}
                placeholder={t('allPrincipleStatuses')}
                style={{ width: 180 }}
                value={principleStatus}
              >
                {PRINCIPLE_STATUSES.map(value => (
                  <Select.Option key={value} value={value}>{t(`statuses.${value}`)}</Select.Option>
                ))}
              </Select>
              <Select
                allowClear
                onChange={resetPage(setInterestStatus)}
                placeholder={t('allInterestStatuses')}
                style={{ width: 180 }}
                value={interestStatus}
              >
                {INTEREST_STATUSES.map(value => (
                  <Select.Option key={value} value={value}>{t(`statuses.${value}`)}</Select.Option>
                ))}
              </Select>
              <Button onClick={handleClearFilters}>{t('clearFilters')}</Button>
            </Space>
            <Button icon={<PlusOutlined />} onClick={() => history.push(`${BASE_PATH}/${NEW_ID}`)} type='primary'>
              {t('create')}
            </Button>
          </Space>

          <Table
            columns={columns}
            dataSource={result.loanAgreements || []}
            loading={loading}
            onChange={handleTableChange}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
              showTotal: total => t('totalCount', { count: total }),
              total: result.totalCount || 0
            }}
            rowKey='id'
            scroll={{ x: 'max-content' }}
            size='small'
          />

          <Drawer
            destroyOnHidden
            onClose={closeDrawer}
            open={Boolean(drawerId)}
            title={formId ? t('editTitle', { id: formId }) : t('newTitle')}
            width={720}
          >
            {drawerId && (
              <LoanAgreementForm
                id={formId}
                key={drawerId}
                onClose={closeDrawer}
                onSaved={handleSaved}
              />
            )}
          </Drawer>
        </ContentLayout>
      </LayoutContentWrapper>
    </Layout>
  )
}

export default LoanAgreementsIndex
